import { Link, useNavigate } from 'react-router-dom';
import { Button, ChatBubble, Logo } from '../design-system/components';
import { useAuth } from '../lib/auth';
import './home.css';

const steps = [
  {
    title: 'Set up a room',
    body: 'Name a chat, add up to 8 buddies with their screen names, and pick when it opens — now, soon, or always-on.',
  },
  {
    title: 'Kids join with a link',
    body: 'Each buddy gets a private link. Kids sign in with a screen name and PIN — no email, no personal details.',
  },
  {
    title: 'Turn on buddy chat (optional)',
    body: 'Let a child message one accepted buddy 1:1, with a daily time budget you set. Off by default — you opt in per child.',
  },
  {
    title: 'You stay in the loop',
    body: 'Every message, in every room and every buddy chat, is supervised and saved. Read the transcript any time.',
  },
];

const safety = [
  {
    icon: '🛡️',
    title: 'Supervision, framed as care',
    body: 'A grown-up keeps every chat safe. Kids always know an adult is looking out for them.',
  },
  {
    icon: '🚫',
    title: 'Smart, server-side moderation',
    body: 'Links and pictures are off by default. Phone numbers and emails are always hidden and flagged for you.',
  },
  {
    icon: '🙈',
    title: 'No personal data for kids',
    body: 'Kids have a screen name and a PIN — that’s it. No email, no profiles, no tracking.',
  },
  {
    icon: '🔐',
    title: 'Built secure from the start',
    body: 'Passwords and PINs are hashed, sessions are short-lived, and everything sensitive is audited.',
  },
];

const faqs = [
  {
    q: 'Is KidzIM actually safe for young kids?',
    a: 'Safety is the whole point. Chats are supervised and saved, sharing is locked down by default, and a grown-up approves who’s in every room.',
  },
  {
    q: 'How do kids sign in without an email?',
    a: 'You create a family login code and give each kid a screen name and a 6-digit PIN. They can also tap a private invite link to join a specific room.',
  },
  {
    q: 'What data do you keep?',
    a: 'As little as possible. Kids have no email or personal info — just a screen name. Chat transcripts are kept for you to review, and we’ll add retention controls soon.',
  },
  {
    q: 'What’s the difference between a chat room and buddy chat?',
    a: 'A chat room is a group chat you create and schedule for a set of buddies. Buddy chat is a 1:1 line between two accepted buddies — off by default, turned on per child, with a daily time budget (and optional allowed hours) you control. Both are just as supervised: you can read the transcript any time.',
  },
  {
    q: 'How much does it cost?',
    a: 'KidzIM is free while we’re in beta. If we add paid plans later, you’ll always have plenty of notice.',
  },
];

export function Home() {
  const { user } = useAuth();
  const nav = useNavigate();

  const primaryCta =
    user && (user.role === 'parent' || user.role === 'admin') ? (
      <Button size="lg" onClick={() => nav('/app')}>
        Go to your dashboard
      </Button>
    ) : (
      <Button size="lg" onClick={() => nav('/parent?mode=register')}>
        Get started — it’s free
      </Button>
    );

  return (
    <div className="home">
      <nav className="home-nav">
        <Link to="/" aria-label="KidzIM home">
          <Logo height={30} />
        </Link>
        <div className="home-nav-links home-nav-hide-mobile">
          <a href="#how">How it works</a>
          <a href="#safety">Safety</a>
          <a href="#faq">FAQ</a>
        </div>
        <div className="home-nav-cta" style={{ marginLeft: 'auto' }}>
          {user ? (
            <Button variant="secondary" size="sm" onClick={() => nav('/app')}>
              Dashboard
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => nav('/parent?mode=login')}>
                Sign in
              </Button>
              <Button size="sm" onClick={() => nav('/parent?mode=register')}>
                Get started
              </Button>
            </>
          )}
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="home-hero">
        <div className="home-hero-inner">
          <h1>
            Safe, supervised chats for <span className="accent">kids and their buddies</span>
          </h1>
          <p>
            KidzIM gives kids the joy of messaging their friends — in group chat rooms you schedule, or
            1:1 buddy chat you turn on — with a grown-up gently keeping every chat safe. You set it up,
            they have fun, and you can read along any time.
          </p>
          <div className="home-hero-cta">
            {primaryCta}
            <Button variant="secondary" size="lg" onClick={() => scrollToId('how')}>
              See how it works
            </Button>
          </div>
          <p className="home-hero-note">Free while in beta · No app to install · Works on iPad, tablet, or computer</p>
        </div>

        <div className="home-hero-preview" aria-hidden>
          <div className="home-bubbles">
            <ChatBubble variant="system">Mom is keeping this chat safe 🛡️</ChatBubble>
            <ChatBubble author="Sam" time="3:14">
              wanna play after school? 🏀
            </ChatBubble>
            <ChatBubble mine time="3:14">
              yes!! meet at the park 🛝
            </ChatBubble>
            <ChatBubble author="Sam" time="3:15">
              🔗 link removed
            </ChatBubble>
          </div>
        </div>
      </header>

      {/* ── How it works ── */}
      <section id="how" className="home-section">
        <span className="kz-eyebrow home-eyebrow">How it works</span>
        <h2>Up and running in minutes</h2>
        <p className="home-section-lead">A few simple steps — no tech skills required.</p>
        <div className="home-steps">
          {steps.map((step, i) => (
            <div className="home-step" key={step.title}>
              <div className="home-step-num">{i + 1}</div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Safety ── */}
      <section id="safety" className="home-safety">
        <div className="home-section">
          <span className="kz-eyebrow home-eyebrow">Safety first</span>
          <h2>Built for peace of mind</h2>
          <p className="home-section-lead">
            There are children involved, so safety isn’t a feature — it’s the reason KidzIM exists.
          </p>
          <div className="home-safety-grid">
            {safety.map((item) => (
              <div className="home-safety-card" key={item.title}>
                <span className="icon" aria-hidden>
                  {item.icon}
                </span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="home-section">
        <span className="kz-eyebrow home-eyebrow">Pricing</span>
        <h2>Free while we’re in beta</h2>
        <p className="home-section-lead">
          Help us build the safest place for kids to chat. No card, no catch — just sign up.
        </p>
        <div className="home-pricing-card">
          <div className="home-price">Free</div>
          <p className="home-price-sub">while KidzIM is in beta</p>
          <ul className="home-price-list">
            <li>Unlimited supervised rooms</li>
            <li>Up to 8 buddies per room</li>
            <li>Optional 1:1 buddy chat, with daily time limits</li>
            <li>Full chat transcripts</li>
            <li>Link &amp; picture controls</li>
            <li>Automatic safety moderation</li>
          </ul>
          <Button full size="lg" onClick={() => nav('/parent?mode=register')}>
            Create your free account
          </Button>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="home-section">
        <span className="kz-eyebrow home-eyebrow">FAQ</span>
        <h2>Questions, answered</h2>
        <div className="home-faq-list">
          {faqs.map((item) => (
            <details className="home-faq-item" key={item.q}>
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="home-footer">
        <div className="home-footer-inner">
          <div className="home-footer-brand">
            <img src="/logo-full.svg" alt="KidzIM" height={30} style={{ filter: 'brightness(0) invert(1)' }} />
            <p>Safe, supervised chat for kids and their friends.</p>
          </div>
          <div>
            <h4>Product</h4>
            <a href="#how">How it works</a>
            <a href="#safety">Safety</a>
            <a href="#pricing">Pricing</a>
          </div>
          <div>
            <h4>Account</h4>
            <Link to="/parent?mode=register">Sign up</Link>
            <Link to="/parent?mode=login">Sign in</Link>
            <Link to="/kid">Kid sign-in</Link>
          </div>
          <div>
            <h4>Company</h4>
            <a href="#faq">FAQ</a>
            <a href="mailto:hello@kidzim.com">Contact</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
        <div className="home-footer-bottom">
          <span>© {new Date().getFullYear()} KidzIM. Made with care for families.</span>
          <span>Keeping kids’ chats safe 🛡️</span>
        </div>
      </footer>
    </div>
  );
}

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}
